import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticInput from 'components/v2/input';

const pageTestId = 'shipper-sop-form';

export default function SopForm({ onSave, onCancel, sop: initialSOP = {} }) {
  const [sop, setSOP] = useState([{ ...initialSOP }]);
  const { t } = useTranslation();
  const handleCancel = () => onCancel();
  const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/;

  const validateErrors = (sopDocs = []) => {
    let isValid = true;
    const validatedItems = sopDocs.map((item) => {
      const errors = {};
      if (!item?.name.trim()) {
        errors.name = { message: t('NAME_IS_REQUIRED') };
      }
      if (!item?.link.trim()) {
        errors.link = { message: t('URL_FIELD_IS_REQUIRED') };
      } else if (!urlPattern.test(item?.link)) {
        errors.link = { message: t('ENTER_VALID_URL') };
      }
      if (Object.keys(errors).length) {
        item.errors = { ...errors };
        isValid = false;
      }
      return item;
    });

    setSOP([...validatedItems]);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateErrors(sop)) {
      let payload = sop.map((item) => {
        const { errors, name, link, ...rest } = item;
        return {
          ...rest,
          name: name.trim(),
          link: link.trim(),
        };
      });
      onSave(payload);
    }
  };

  const handleFieldChange = (index, field, value) => {
    setSOP((prevSOP) =>
      prevSOP.map((item, i) =>
        i === index ? { ...item, [field]: value, errors: {} } : item,
      ),
    );
  };

  const handleDeleteSop = (index) => {
    setSOP((prevSOP) => {
      const newSOP = [...prevSOP];
      newSOP.splice(index, 1);
      if (newSOP.length === 0) {
        newSOP.push({ name: '', link: '' });
      }
      return newSOP;
    });
  };

  const handleAddSOP = () => {
    setSOP((prevSOP) => [...prevSOP, { ...initialSOP }]);
  };

  return (
    <form noValidate onSubmit={handleSubmit} data-testid={pageTestId}>
      {sop.map((item, i) => (
        <div key={`sop-item-${i}`} data-testid={`${pageTestId}-item`}>
          <div className="flex -mx-2">
            <div className="flex-1 px-2 mb-4">
              <CargomaticInput
                error={item?.errors?.name}
                id={`${pageTestId}-name-input`}
                type="name"
                label={t('NAME')}
                placeholder={t('ENTER_NAME')}
                value={item?.name || ''}
                onChange={(value) => handleFieldChange(i, 'name', value)}
                maxLength={40}
              />
            </div>
          </div>
          <div className="flex -mx-2">
            <div className="flex-1 px-2 mb-4">
              <CargomaticInput
                error={item?.errors?.link}
                id={`${pageTestId}-url-input`}
                type="link"
                label={t('URL')}
                placeholder={t('ENTER_URL')}
                value={item?.link || ''}
                onChange={(value) => handleFieldChange(i, 'link', value)}
              />
            </div>
          </div>
          {i > 0 && (
            <DeleteButton
              className="mt-2 flex-none"
              onClick={() => handleDeleteSop(i)}
              id={`${pageTestId}-delete`}
            />
          )}
          <hr />
        </div>
      ))}
      <div
        className="flex justify-end mt-4 mb-4"
        data-testid={`${pageTestId}-add`}
      >
        <AddButton
          onClick={handleAddSOP}
          disabled={sop[0]?.id}
          id={`${pageTestId}-add-button`}
        />
      </div>
      <div
        className="flex justify-end mb-4"
        data-testid={`${pageTestId}-footer`}
      >
        <button
          type="submit"
          className="button button-green"
          data-testid={`${pageTestId}-save-button`}
        >
          {titleize(t('SAVE'))}
        </button>
        <button
          onClick={handleCancel}
          className="button button-secondary"
          data-testid={`${pageTestId}-cancel-button`}
        >
          {t('CANCEL')}
        </button>
      </div>
    </form>
  );
}

SopForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  sop: PropTypes.array.isRequired,
};
